import { AlertTriangle, Check, KeyRound, LoaderCircle, Power, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { apiClient, type AiModel } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { RequiredFieldsHint, missingFields } from "@/shared/components/RequiredFieldsHint";
import { formatShortDate } from "@/shared/utils/format";
import { deactivateVersaAiSetting, getVersaAiSetting, saveVersaAiSetting, type VersaAiSetting } from "../api";
import { versaEnabled } from "../config";
import "../versa.css";

/**
 * A seção "Versa" da aba Provedores e chaves de Administração → Modelos
 * de IA, logo abaixo da do Mestra Help e no mesmo formato (era uma aba
 * própria até 2026-10-21): chave do Gemini e modelo PRÓPRIOS da análise
 * dos relatórios, separados dos do Copiloto (decisão de 2026-10-19). Sem
 * config ativa, a geração do Versa fica desligada — não cai na do
 * Copiloto.
 *
 * Mesmas regras do cartão de provedor da tela: o campo de chave nasce
 * SEMPRE vazio (vazio = mantém a gravada; preencher com a máscara faria
 * um salvar distraído gravar `••••3456` como chave), e o botão só libera
 * quando dá pra salvar, dizendo o que falta.
 */

/** Espelha `VERSA_AI_PROVIDER` do back: a geração usa o cliente Gemini. */
const VERSA_AI_PROVIDER = "gemini";

/** Com o Versa desligado a seção não aparece — a aba fica só com o
 *  Mestra Help e os provedores de classificação. */
export function VersaAiSection({ token }: { token: string }) {
  if (!versaEnabled) return null;
  return <VersaAiCard token={token} />;
}

function VersaAiCard({ token }: { token: string }) {
  const [atual, setAtual] = useState<VersaAiSetting | null>(null);
  const [modelos, setModelos] = useState<AiModel[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [chave, setChave] = useState("");
  const [modeloId, setModeloId] = useState("");

  const carregar = async () => {
    try {
      const [config, catalogo] = await Promise.all([getVersaAiSetting(token), apiClient.listAiModels(token)]);
      setAtual(config);
      setModelos(catalogo.filter(modelo => modelo.is_active && modelo.provider === VERSA_AI_PROVIDER));
      setModeloId(config ? String(config.model_id) : "");
      setErro(null);
    } catch (error) {
      setErro(errorMessage(error, "Não foi possível carregar a IA do Versa."));
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const ativo = atual?.is_active ?? false;
  const campos = [
    // Já cadastrada (mesmo desligada), a chave pode ficar em branco.
    { label: "Chave de API", filled: Boolean(atual) || chave.trim().length > 0 },
    { label: "Modelo", filled: modeloId !== "" }
  ];
  const mudou = chave.trim().length > 0 || !ativo || String(atual?.model_id ?? "") !== modeloId;
  const podeSalvar = !salvando && missingFields(campos).length === 0 && mudou;

  const salvar = async () => {
    if (!podeSalvar) return;
    setSalvando(true);
    setErro(null);
    setAviso(null);
    try {
      const salvo = await saveVersaAiSetting(token, {
        ...(chave.trim() ? { api_key: chave.trim() } : {}),
        model_id: Number(modeloId)
      });
      setAtual(salvo);
      setChave("");
      setAviso("IA do Versa salva.");
    } catch (error) {
      setErro(errorMessage(error, "Não foi possível salvar a IA do Versa."));
    } finally {
      setSalvando(false);
    }
  };

  const desligar = async () => {
    setSalvando(true);
    setErro(null);
    setAviso(null);
    try {
      await deactivateVersaAiSetting(token);
      await carregar();
      setAviso("IA do Versa desligada.");
    } catch (error) {
      setErro(errorMessage(error, "Não foi possível desligar a IA do Versa."));
    } finally {
      setSalvando(false);
    }
  };

  const autor = atual?.updated_by_name || atual?.updated_by;
  // O modelo gravado que saiu do catálogo ativo entra como opção órfã,
  // senão o seletor mostraria "escolha" numa config que tem modelo.
  const orfao = atual && !modelos.some(modelo => modelo.id === atual.model_id);

  return (
    <section className="ai-admin-section" aria-labelledby="ai-section-versa">
      <h3 id="ai-section-versa">
        <Sparkles size={16} aria-hidden="true" /> Versa — análise dos relatórios
      </h3>
      <p>
        A IA que escreve as análises e sínteses dos relatórios do Versa. Chave e modelo próprios, separados
        dos do Copiloto. Sem esta configuração, a geração no Versa fica desligada (a Emergência IA continua
        funcionando).
      </p>

      {carregando ? (
        <ApiState kind="loading">Carregando a IA do Versa…</ApiState>
      ) : (
        <div className={`card ai-provider-card ${ativo ? "is-active" : atual ? "is-inactive" : "is-empty"}`}>
          <div className="ai-provider-head">
            <span className="ai-provider-icon" aria-hidden="true">
              <KeyRound size={18} />
            </span>
            <div className="ai-provider-title">
              <h3>Gemini do Versa</h3>
              <p>
                {ativo ? (
                  <>
                    Chave <code className="ai-provider-hint">{atual?.api_key_hint}</code>
                    {atual?.model_name ? ` · modelo ${atual.model_name}` : ""}
                    {autor ? ` · por ${autor}` : ""}
                    {atual?.updated_at ? ` em ${formatShortDate(new Date(atual.updated_at))}` : ""}
                  </>
                ) : atual ? (
                  "Desligada — salve para religar."
                ) : (
                  "Nenhuma chave cadastrada."
                )}
              </p>
            </div>
            {ativo ? (
              <span className="badge-pill success">Ativa</span>
            ) : (
              <span className="badge-pill inactive">{atual ? "Desligada" : "Sem chave"}</span>
            )}
          </div>

          {erro && (
            <p className="form-error" role="alert">
              {erro}
            </p>
          )}
          {aviso && (
            <p className="versa-admin-feedback" role="status">
              {aviso}
            </p>
          )}
          {atual?.unreadable && (
            <p className="form-error" role="alert">
              <AlertTriangle size={14} aria-hidden="true" /> A chave gravada não pode ser lida com a chave-mestra
              deste ambiente. Digite-a novamente para regravá-la.
            </p>
          )}

          <div className="ai-provider-fields">
            <div className="admin-field-group ai-provider-key">
              <label className="field">
                <span>Chave de API do Gemini</span>
                <input
                  type="password"
                  autoComplete="off"
                  value={chave}
                  placeholder={atual ? "Deixe em branco para manter a atual" : "Cole a chave aqui"}
                  onChange={event => setChave(event.target.value)}
                />
              </label>
              <small className="admin-field-help">
                A chave é guardada cifrada e nunca volta para a tela — só os últimos dígitos.
              </small>
            </div>
            <label className="field">
              <span>Modelo</span>
              <select value={modeloId} disabled={salvando} onChange={event => setModeloId(event.target.value)}>
                <option value="">Escolha um modelo Gemini…</option>
                {orfao && (
                  <option value={String(atual.model_id)}>
                    {atual.model_name ?? `#${atual.model_id}`} (desativado)
                  </option>
                )}
                {modelos.map(modelo => (
                  <option key={modelo.id} value={String(modelo.id)}>
                    {modelo.label ? `${modelo.label} (${modelo.name})` : modelo.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {modelos.length === 0 && (
            <small className="form-hint" role="note">
              Nenhum modelo Gemini ativo no catálogo. Cadastre um na aba Modelos.
            </small>
          )}
          <RequiredFieldsHint fields={campos} />

          <div className="copiloto-admin-actions">
            {ativo && (
              <button className="secondary-button" type="button" disabled={salvando} onClick={() => void desligar()}>
                <Power size={16} aria-hidden="true" /> Desligar
              </button>
            )}
            <button className="primary-button" type="button" disabled={!podeSalvar} onClick={() => void salvar()}>
              {salvando ? <LoaderCircle className="spin" size={16} /> : <Check size={16} />} Salvar
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
